import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Xmark } from '@gravity-ui/icons';
import { Button } from '@heroui/react';
import { useViewerStore } from '@stores/viewerStore';
import { CreatorShareCard } from './CreatorShareCard';
import { copyText } from '@utils/clipboard';

type CopyState = 'idle' | 'copied' | 'failed' | 'permission-denied';

/**
 * 分享面板入口：按角色分流。
 * - owner → 完整分享管理卡
 * - 其他角色 → 简洁复制链接面板
 */
export function SharePanel() {
  const isOpen = useViewerStore((s) => s.sharePanelOpen);
  const sceneRole = useViewerStore((s) => s.sceneRole);

  if (!isOpen) return null;

  // 仅拥有者看到完整分享卡
  if (sceneRole === 'owner') return <CreatorShareCard />;

  // 普通用户 / 匿名：简洁复制链接
  return <ViewerSharePanel />;
}

/** 普通用户简洁分享面板（仅复制链接）。 */
function ViewerSharePanel() {
  const { t } = useTranslation();
  const sceneConfig = useViewerStore((s) => s.sceneConfig);
  const shortUrl = useViewerStore((s) => s.shortUrl);
  const setOpen = useViewerStore((s) => s.setSharePanelOpen);
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const shareUrl = shortUrl || window.location.href;

  useEffect(() => {
    if (copyState === 'idle') return;
    const timer = window.setTimeout(() => setCopyState('idle'), 1800);
    return () => window.clearTimeout(timer);
  }, [copyState]);

  function handleCopy() {
    void copyText(shareUrl)
      .then((result) =>
        setCopyState(
          result.ok
            ? 'copied'
            : result.reason === 'permission-denied'
              ? 'permission-denied'
              : 'failed',
        ),
      )
      .catch(() => setCopyState('failed'));
  }

  return (
    <>
      <button
        type="button"
        className="absolute inset-0 z-40 cursor-default appearance-none border-none bg-transparent p-0"
        aria-label="Close share panel"
        onClick={() => setOpen(false)}
      />
      <div className="absolute right-14 bottom-4 z-50 w-96 max-w-[calc(100vw-7rem)] rounded-2xl bg-white/10 p-5 shadow-xl backdrop-blur-md">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="text-base font-semibold text-white">Share scene</h3>
            <p className="mt-1 text-xs text-white/50">Copy the current link to share this view.</p>
          </div>
          <Button
            isIconOnly
            size="md"
            onPress={() => setOpen(false)}
            aria-label="Close share panel"
            className="ml-2 flex-shrink-0 rounded-full text-white/40 hover:text-white"
          >
            <Xmark width={14} height={14} />
          </Button>
        </div>

        {sceneConfig && (
          <div className="mb-4 rounded-xl border border-white/10 bg-white/10 p-3">
            <p className="truncate text-sm font-medium text-white">{sceneConfig.name}</p>
            {sceneConfig.creator && (
              <p className="mt-1 truncate text-xs text-white/50">By {sceneConfig.creator.name}</p>
            )}
          </div>
        )}

        <div className="rounded-xl bg-black/20 p-3 text-xs break-all text-white/70">{shareUrl}</div>

        <Button
          fullWidth
          size="md"
          onPress={handleCopy}
          className="mt-3 rounded-full bg-white text-base font-medium text-[#18181B]"
        >
          Copy link
        </Button>

        {copyState !== 'idle' && (
          <div className="absolute right-0 bottom-full mb-3 rounded-full bg-[#18181B]/90 px-4 py-2 text-xs text-white shadow-lg">
            {copyState === 'copied'
              ? t('share.link-copied')
              : copyState === 'permission-denied'
                ? t('share.copy-permission-denied')
                : t('share.copy-failed')}
          </div>
        )}
      </div>
    </>
  );
}
